<template>
	<div class="grid-flex-container pa-4">
		<v-container class="py-6" fluid>
			<v-row>
				<div style="flex-grow: 1">
					<v-text-field
						v-if="canView"
						v-model="search"
						class="py-0 my-0 mb-2 mr-2"
						append-icon="mdi-magnify"
						label="Search"
						single-line
						hide-details
					/>
				</div>
				<v-menu
					content-class="grid-toolbar-menu"
					:close-on-content-click="true"
					offset-y
				>
					<template #activator="{ on, attrs, value }">
						<v-btn
							v-if="canAdd"
							v-bind="attrs"
							v-on="on"
							class="settings-menu-btn"
							color="primary"
						>
							Add
							<v-icon>mdi-Add</v-icon>
							<v-icon v-if="value">mdi-menu-up</v-icon>
							<v-icon v-else>mdi-menu-down</v-icon>
						</v-btn>
					</template>
					<v-list>
						<v-list-item single @click="onMultipleAdd">
							<v-list-item-title
								>Add multiple items</v-list-item-title
							>
						</v-list-item>
						<v-list-item single @click="onSingleAdd">
							<v-list-item-title
								>Add a single item</v-list-item-title
							>
						</v-list-item>
					</v-list>
				</v-menu>
			</v-row>
		</v-container>
		<div class="generic-grid-container">
			<v-data-table
				v-if="canView"
				dense
				:items="activityItems"
				:headers="headers"
				:search.sync="search"
				:sortable="false"
				:loading="loading"
				disable-pagination
				fixed-header
				hide-default-footer
				loading-text="Loading..."
			>
				<template v-slot:[`item.StartDate`]="{ item }">
					<span>{{ getDateTimeFormat(item.StartDate) }}</span>
				</template>
				<template v-slot:[`item.EndDate`]="{ item }">
					<span>{{ getDateTimeFormat(item.EndDate) }}</span>
				</template>
				<template v-slot:[`item.AckByClient`]="{ item }">
					<span>{{ getDateTimeFormat(item.AckByClient) }}</span>
				</template>
				<template
					v-for="staticField in staticFields"
					v-slot:[`item.${staticField.Id}`]="{ item }"
				>
					{{ getStaticFieldValue(staticField, item) }}
				</template>
				<template
					v-for="column in conditionFieldsList"
					v-slot:[`item.${column.Id}`]="{ item }"
				>
					{{ getListValue(column, item) }}
				</template>
				<template v-slot:[`item.ReqClientAck`]="{ item }">
					<v-icon color="primary" v-if="item.ReqClientAck"
						>mdi-check-circle</v-icon
					>
					<v-icon color="error" v-else>mdi-close-circle</v-icon>
				</template>
				<template v-slot:[`item.Status`]="{ item }">
					<span>{{ getItemStatus(item.Status) }}</span>
				</template>
				<template v-slot:[`item.UserId`]="{ item }">
					<span>{{
						getUserFormat(
							item.FirstName,
							item.LastName,
							item.Email,
							item.Username
						)
					}}</span>
				</template>
				<template v-slot:[`item.actions`]="{ item }">
					<v-btn
						v-if="canEdit"
						fab
						small
						icon
						elevation="1"
						class="action-btn"
						@click.stop="editItem(item)"
					>
						<v-icon color="primary" small>mdi-pencil</v-icon>
					</v-btn>
					<v-btn
						v-if="canDelete"
						fab
						small
						icon
						elevation="1"
						class="action-btn"
						@click.stop="deleteItem(item.Id)"
					>
						<v-icon color="error" small>mdi-delete</v-icon>
					</v-btn>
				</template>
			</v-data-table>
		</div>
		<activity-item-modal
			v-if="selectedItem && showDialog"
			:showDialog="showDialog"
			:activityItem="selectedItem"
			@close="closeItemModal()"
		/>
		<activity-add-bulk-item-modal
			v-if="showBulkDialog && selectedItem"
			:showDialog="showBulkDialog"
			:activityItem="selectedItem"
			@close="closeItemModal()"
		/>
	</div>
</template>
<script>
import ConfirmDialog from '@/components/core/dialogs/confirmDialog';
import projectSettingsMixin from '../../../../mixins/projectSettingsMixin';
import { activityStatus } from '../../../../enums/activityStatus';
import ActivityItemModal from './activityItemModals/ActivityItemModal.vue';
import ActivityAddBulkItemModal from './activityItemModals/ActivityAddBulkItemModal.vue';
import { mapGetters } from 'vuex';
import { ActivityItemGetters } from '../../../../store/getters';
import { ActivityItemActions } from '../../../../store/actions';
import permissionsMixin from '../../../../mixins/permissionsMixin';
import { Permissions } from '../../../../enums/permissionsEnum';
export default {
	name: 'ActivityItem',
	components: { ActivityItemModal, ActivityAddBulkItemModal },
	mixins: [projectSettingsMixin, permissionsMixin],
	data() {
		return {
			showDialog: false,
			showBulkDialog: false,
			selectedItem: null,
			search: '',
		};
	},
	computed: {
		...mapGetters({
			activityItems: [ActivityItemGetters.ACTIVITY_ITEMS],
			conditionFieldsList: [ActivityItemGetters.CONDITION_FIELDS_LIST],
			staticFields: [ActivityItemGetters.STATIC_FIELDS],
			loading: [ActivityItemGetters.LOADING],
		}),
		headers() {
			let result = [
				{ text: 'Start Date', value: 'StartDate', width: '1px' },
				{ text: 'End Date', value: 'EndDate', width: '1px' },
				{ text: 'Ack By Client', value: 'AckByClient', width: '1px' },
			];
			if (this.staticFields) {
				this.staticFields.forEach((sf) => {
					result.push({ text: sf.Name, value: sf.Id });
				});
			}
			if (this.conditionFieldsList) {
				this.conditionFieldsList.forEach((list) => {
					result.push({ text: list.Label, value: list.Id });
				});
			}
			result.push(
				{ text: 'Req Client Ack', value: 'ReqClientAck', width: '1px' },
				{ text: 'Status', value: 'Status', width: '1px' },
				{ text: 'User', value: 'UserId', width: '1px' },
				{ text: '', value: 'actions', sortable: false, align: 'end' }
			);
			return result;
		},
		canView() {
			return this.hasPermissions([
				Permissions.Tools.ActivityBuilder.Items.View,
			]);
		},
		canAdd() {
			return this.hasPermissions([
				Permissions.Tools.ActivityBuilder.Items.Add,
			]);
		},
		canEdit() {
			return this.hasPermissions([
				Permissions.Tools.ActivityBuilder.Items.Edit,
			]);
		},
		canDelete() {
			return this.hasPermissions([
				Permissions.Tools.ActivityBuilder.Items.Delete,
			]);
		},
	},
	mounted() {
		this.loadData();
	},
	destroyed() {
		this.$store.dispatch(ActivityItemActions.CLEAR);
	},
	methods: {
		loadData() {
			this.$store.dispatch(
				ActivityItemActions.LOAD_ACTIVITY_ITEMS,
				this.$route.params.id
			);
			this.$store.dispatch(
				ActivityItemActions.LOAD_FIELDS,
				this.$route.params.id
			);
		},
		getStaticFieldValue(column, item) {
			if (!item.StaticFields) return null;
			if (item.StaticFields.length == 0) return null;
			if (
				!item.StaticFields.find(
					(x) => x.ActivityStaticFieldId == column.Id
				)
			)
				return null;
			return item.StaticFields.find(
				(x) => x.ActivityStaticFieldId == column.Id
			).Value;
		},
		getItemStatus(status) {
			switch (status) {
				case activityStatus.Pending: {
					return 'Pending';
				}
				case activityStatus.Completed: {
					return 'Completed';
				}
				case activityStatus.DataReady: {
					return 'Data Ready';
				}
			}
		},
		getListValue(list, item) {
			let result = [];
			list.GridViewFieldIds.forEach((l) => {
				let groupBy = item.GroupByCriteria.find(
					(x) => x.ActivityGroupByConditionFieldGridViewFieldId == l
				);
				if (groupBy) result.push(groupBy.Value);
			});
			return result.join(' / ');
		},
		onSingleAdd() {
			this.selectedItem = { StaticFields: [], GroupByCriteria: [] };
			this.showDialog = true;
		},
		onMultipleAdd() {
			this.selectedItem = { StaticFields: [], GroupByCriteria: [] };
			this.showBulkDialog = true;
		},
		editItem(item) {
			this.selectedItem = item;
			this.showDialog = true;
		},
		deleteItem(id) {
			ConfirmDialog({
				message: 'Are you sure you want to delete this item?',
			}).then((isDelete) => {
				if (isDelete) {
					this.$store.dispatch(ActivityItemActions.DELETE, id);
				}
			});
		},
		closeItemModal() {
			this.showDialog = false;
			this.showBulkDialog = false;
			this.selectedItem = null;
			this.loadData();
		},
	},
};
</script>
